import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { useState } from "react"
import { ReviewAssociations, reviewStatusKeys } from "./ReviewAssociations"
import { SavedOriginalActions } from "./SavedOriginalActions"
import { pilotRequests, type ReviewStatus } from "./pilotRequestsApi"

export function ArticleReviewInbox() {
  const enabled = useFeatureFlag("COMPANY_INDEX")
  const handoff = useFeatureFlag("COMPANY_INDEX_ARTICLE_HANDOFF")
  const { hasPermission } = useUser()
  if (
    !enabled ||
    !handoff ||
    !hasPermission(PERMISSIONS.MC_DECLARATIONS_VIEW) ||
    !hasPermission(PERMISSIONS.MC_DECLARATIONS_REVIEW)
  )
    return null
  return <Inbox />
}
function Inbox() {
  const { user } = useUser()
  const { translate: t } = useTranslation()
  const cache = useQueryClient()
  const [status, setStatus] = useState<ReviewStatus>("pending")
  const rows = useInfiniteQuery({
    queryKey: ["company-index", user?.organization, "review-inbox", status],
    queryFn: ({ pageParam }) => pilotRequests.reviewInbox(status, pageParam),
    initialPageParam: 0,
    getNextPageParam: (page, pages) =>
      page.length === 100 ? pages.length * 100 : undefined,
  })
  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string
      decision: "approved" | "rejected"
    }) => pilotRequests.decide(id, decision),
    onSuccess: () => {
      void cache.invalidateQueries({
        queryKey: ["company-index", user?.organization],
      })
    },
    onError: () => {
      void rows.refetch()
    },
  })
  return (
    <section className="space-y-4" aria-label={t("CI_REVIEW_INBOX")}>
      <h2 className="text-xl font-semibold">{t("CI_REVIEW_INBOX")}</h2>
      <p className="text-sm text-muted-foreground">
        {t("CI_REVIEW_INBOX_HINT")}
      </p>
      <div className="flex flex-wrap items-end gap-3">
        <label>
          {t("CI_REVIEW_STATUS")}
          <select
            className="block rounded-md border bg-background p-2"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as ReviewStatus)
              decide.reset()
            }}
          >
            {(["pending", "approved", "rejected"] as const).map((s) => (
              <option key={s} value={s}>
                {t(reviewStatusKeys[s])}
              </option>
            ))}
          </select>
        </label>
        <Button
          variant="outline"
          disabled={rows.isFetching}
          onClick={() => void rows.refetch()}
        >
          {t("CI_REFRESH")}
        </Button>
      </div>
      {rows.isPending && <p>{t("CI_LOADING")}</p>}
      {rows.isError && <p role="alert">{t("CI_ERROR")}</p>}
      {decide.isError && <p role="alert">{t("CI_REVIEW_DECISION_ERROR")}</p>}
      {decide.isSuccess && (
        <p role="status">
          {t("CI_REVIEW_DECISION_SAVED")}:{" "}
          {t(reviewStatusKeys[decide.data.status])}
        </p>
      )}
      {rows.isSuccess && !rows.data.pages.flat().length && (
        <p>{t("CI_REVIEW_INBOX_EMPTY")}</p>
      )}
      {!!rows.data?.pages.flat().length && (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                <th className="p-3">{t("CI_REQUEST_TITLE")}</th>
                <th className="p-3">{t("CI_REVIEW_SUPPLIER")}</th>
                <th className="p-3">{t("CI_MATCH_DOCUMENT")}</th>
                <th className="p-3">{t("CI_REVIEW_ASSOCIATIONS")}</th>
                <th className="p-3">{t("CI_REVIEW_STATUS")}</th>
                <th className="p-3">{t("CI_REVIEW_ACTIONS")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.data.pages.flat().map((row) => (
                <tr key={row.id} className="border-t align-top">
                  <td className="p-3">
                    {row.requestTitle}
                    <p className="text-xs text-muted-foreground">
                      {row.submittedBy} ·{" "}
                      {new Date(row.submittedAt).toLocaleString()}
                    </p>
                  </td>
                  <td className="p-3">
                    {row.companyName}
                    <p className="text-xs text-muted-foreground">
                      {row.issuerDomain}
                    </p>
                  </td>
                  <td className="p-3">
                    <p className="break-all">{row.filename}</p>
                    <SavedOriginalActions
                      id={row.candidateId}
                      filename={row.filename}
                    />
                  </td>
                  <td className="p-3">
                    <ReviewAssociations matches={row.matches} />
                  </td>
                  <td className="p-3">
                    {t(reviewStatusKeys[row.status])}
                    {row.reviewedBy && (
                      <p className="text-xs text-muted-foreground">
                        {row.reviewedBy} ·{" "}
                        {row.reviewedAt &&
                          new Date(row.reviewedAt).toLocaleString()}
                      </p>
                    )}
                  </td>
                  <td className="p-3">
                    {row.status === "pending" && (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          disabled={decide.isPending}
                          onClick={() =>
                            decide.mutate({ id: row.id, decision: "approved" })
                          }
                        >
                          {t("CI_REVIEW_APPROVE")}
                        </Button>
                        <Button
                          variant="outline"
                          disabled={decide.isPending}
                          onClick={() =>
                            decide.mutate({ id: row.id, decision: "rejected" })
                          }
                        >
                          {t("CI_REVIEW_REJECT")}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {rows.hasNextPage && (
        <Button
          variant="outline"
          disabled={rows.isFetchingNextPage}
          onClick={() => void rows.fetchNextPage()}
        >
          {t("CI_MORE")}
        </Button>
      )}
    </section>
  )
}
